import { create } from 'zustand';

/**
 * MV5 Sprint 2.D — view-mode state for the Learning Item Shell.
 *
 * Three modes, each a distinct layout thesis:
 *
 *   guided   — Companion drives; minimal chrome. Video main; AI pane
 *              collapsible from the Companion dock; no Peek/Mini-IDE chips.
 *              Default for every learner.
 *
 *   standard — Split-panel: lecture player + AI pane always expanded
 *              (~400px right). Peek Player chip lives in the AI pane;
 *              Mini-IDE chip lives in the lecture footer. This is the
 *              layout the MV5 spec calls the "daily driver".
 *
 *   studio   — Full IDE + lecture in a corner PIP. Environment sandbox
 *              provisioned via env-orchestrator. Peek/Mini-IDE folded
 *              back into their overlay pair; the IDE has focus.
 *
 * The mode is:
 *   1. Loaded from student_profiles.view_mode via GET /users/me/preferences
 *      (bootstrap in the Layout).
 *   2. Written to localStorage for offline persistence + instant reload.
 *   3. Mirrored back to the server via PATCH /users/me/preferences on any
 *      change so the next login on a different device sees it.
 *
 * `firstStudioAt` gates a one-time Studio coach-mark. Once the server has
 * stamped it (idempotent — the DB CHECK the timestamp is latched once),
 * the coach-mark stops rendering.
 */

export type ViewMode = 'guided' | 'standard' | 'studio';

const LOCAL_STORAGE_KEY = '__hauraki_view_mode__';

function readInitialViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'guided';
  const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  if (raw === 'guided' || raw === 'standard' || raw === 'studio') return raw;
  return 'guided';
}

interface ViewModeState {
  viewMode: ViewMode;
  firstStudioAt: string | null;
  /**
   * Track whether the Studio coach-mark should currently render. This is a
   * session-scoped decision: on first switch to Studio in this session,
   * show it; after the user dismisses it (or after firstStudioAt is stamped
   * on a prior session), keep it hidden.
   */
  studioCoachDismissed: boolean;

  /**
   * Set the mode. Persists to localStorage synchronously; the async server
   * sync is handled by the caller (the shell effect) so this store stays
   * free of network side-effects.
   */
  setViewMode: (next: ViewMode) => void;
  /**
   * Called by the Layout bootstrap once GET /users/me/preferences resolves.
   * Overwrites the local value with the server-authoritative one if they
   * differ; otherwise no-op.
   */
  hydrateFromServer: (next: ViewMode, firstStudioAt: string | null) => void;
  dismissStudioCoach: () => void;
}

export const useViewModeStore = create<ViewModeState>((set, get) => ({
  viewMode: readInitialViewMode(),
  firstStudioAt: null,
  studioCoachDismissed: false,

  setViewMode: (next) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, next);
    }
    // If we're entering Studio and there's no firstStudioAt on file yet,
    // that means this session is the "first Studio session" — the coach-
    // mark should render. It only actually renders when
    // studioCoachDismissed is false AND firstStudioAt is null.
    const prev = get().viewMode;
    set({
      viewMode: next,
      studioCoachDismissed:
        next === 'studio' && prev !== 'studio'
          ? false
          : get().studioCoachDismissed,
    });
  },

  hydrateFromServer: (next, firstStudioAt) => {
    // Never overwrite a user's in-flight change; only accept the server
    // value if it disagrees with what we've stored. The local write path
    // above always fires before the network round-trip, so a disagreement
    // means the server row was updated on another device.
    const current = get().viewMode;
    if (next !== current && typeof window !== 'undefined') {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, next);
    }
    set({
      viewMode: next,
      firstStudioAt,
      // If the server has a firstStudioAt already, the coach-mark should
      // stay dismissed forever.
      studioCoachDismissed:
        firstStudioAt !== null ? true : get().studioCoachDismissed,
    });
  },

  dismissStudioCoach: () => set({ studioCoachDismissed: true }),
}));
